import { Button, Dropdown, Input, message, Space, Table, TablePaginationConfig, TableProps, Tooltip } from "antd"
import SearchIcon from "../../../components/icons/SearchIcon"
import ProductIcon from "../../../components/icons/ProductIcon";
import { Link } from "react-router-dom";
import OpenIcon from "../../../components/icons/OpenIcon";
import ThreeDotsIcon from "../../../components/icons/ThreeDotsIcon";
import SortIcon from "../../../components/icons/SortIcon";
import FilterIcon from "../../../components/icons/FilterIcon";
import { useEffect, useMemo, useRef, useState } from "react";
import DeleteModalSimple from "./DeleteModalSimple";
import AddEditProductModal from "./modals/AddEditProductModal";
import { DeleteBrandProduct, GetBrandProducts } from "../../../API/endpoints/Brands";
import { productCategories, productSubCategories } from "../../../constants/dataConstants";
import { removeDuplicatesByValue } from "../../../API/axios";
import { useBrandTabsContext } from "../contexts/BrandTabsContext";
import { TAB_ASSETS } from "../../../constants/constants";

interface Props {
    brand: any
}
const Products = (props: Props) => {

    const [messageApi, contextHolder] = message.useMessage()
    const { setActiveTab, setProductID, reloadProducts } = useBrandTabsContext();

    const [isAddProduct, setIsAddProduct] = useState(false);
    const [isEditProduct, setIsEditProduct] = useState<any>({})
    const [isDeleteItem, setIsDeleteItem] = useState<any>({});

    const [data, setData] = useState<any[]>([]);
    const [pageNumber, setPageNumber] = useState<number | undefined>(1);
    const [totalCount, setTotalCount] = useState(0);
    const [searchKey, setSearchKey] = useState("");
    const [selectedCategory, setSelectedCategory] = useState<string[]>([]);
    const [selectedSubCategory, setSelectedSubCategory] = useState<string[]>([]);
    const [sortOrder, setSortOrder] = useState("");
    const [loading, setLoading] = useState(true);
    const [reload, setReload] = useState(false);

    const tableRef = useRef<any>(null);
    const [scrollY, setScrollY] = useState(0);

    useEffect(() => {
        setLoading(true);
        let where: any = {};
        let sort: any = {};
        if (selectedCategory.length > 0) where = { ...where, category: selectedCategory }
        if (selectedSubCategory.length > 0) where = { ...where, sub_category: selectedSubCategory }
        if (sortOrder) sort = { name: sortOrder }
        GetBrandProducts(props?.brand?.id, pageNumber || 1, searchKey, where, sort)
            .then((response) => {
                setData(response?.data?.rows);
                setTotalCount(response?.data?.count);
                setLoading(false);
            })
            .catch((error) => {
                setLoading(false);
                console.log(error);
            });
    }, [props?.brand, searchKey, pageNumber, reload, selectedCategory, selectedSubCategory, reloadProducts])

    useEffect(() => {
        const updateScrollY = () => {
            if (tableRef.current) {
                const containerHeight = tableRef.current.clientHeight;
                const headerHeight = 48; // Adjust this value if your header is bigger/smaller
                setScrollY(containerHeight - headerHeight);
            }
        };

        updateScrollY();
        window.addEventListener('resize', updateScrollY);
        return () => window.removeEventListener('resize', updateScrollY);
    }, []);

    const onSuccessAddProduct = (data: any) => {
        if (!isEditProduct?.name) {
            setData((prev: any[]) => [data, ...prev]);
        } else {
            setReload(!reload)
        }

        setTotalCount(totalCount + 1);
        messageApi.success(isEditProduct?.name ? "Product updated successfully" : "Added new product successfully");
        setIsEditProduct({});
        setIsAddProduct(false);
    }

    const onCancelAddProduct = () => {
        setIsEditProduct({});
        setIsAddProduct(false);
    }

    const onSuccessDeleteProduct = () => {
        messageApi.success("Product deleted successfully");
        const newList = data.filter((item: any) => item?.id !== isDeleteItem?.id)
        setData([...newList])
        setIsDeleteItem({});
    }

    const onTableChange = (pagination: TablePaginationConfig, filters: any, sorter: any) => {
        setPageNumber(pagination?.current)
        setSelectedCategory(filters?.category || [])
        setSelectedSubCategory(filters?.sub_category || [])
        if (sorter?.field === "name") {
            setSortOrder(sorter?.order === "ascend" ? "ASC" : sorter?.order === "descend" ? "DESC" : "")
        } else {
            setSortOrder("")
        }
    }

    const openLink = (link: string) => {
        const fullUrl = link.startsWith("http") ? link : `https://${link}`;
        window.open(fullUrl, "_blank", "noopener,noreferrer");
    }

    const onManageDocReq = (id: string) => {
        setActiveTab(TAB_ASSETS)
        setProductID(id)
    }

    const subCategories = useMemo(() => {
        const list = selectedCategory?.length > 0 ? productSubCategories?.filter((c: any) => selectedCategory.includes(c?.category)) : productSubCategories
        return removeDuplicatesByValue(list)?.map((c: any) => ({ text: c?.text, value: c?.value }))
    }, [selectedCategory]);

    const actionOptions = (record: any) => {
        let items: any = [];
        items.push(
            {
                key: '1',
                label: "Edit product data",
                onClick: () => {
                    setIsEditProduct(record)
                    setIsAddProduct(true);
                }
            }
        )

        items.push(
            {
                key: '2',
                label: "Manage product assets",
                onClick: () => {
                    onManageDocReq(record?.id)
                }
            }
        )


        items.push({
            key: '4',
            danger: true,
            label: "Delete product",
            onClick: () => setIsDeleteItem(record)
        })

        return items;
    }

    const columns: TableProps['columns'] = [
        {
            title: (
                <Space>
                    <p className="m-0">Products</p>
                    <p className="bg-[#667085] text-white px-[3px] py-[4px] min-w-[20px] text-center rounded-[24px] text-[10px] leading-[10px] m-0">{totalCount?.toString().padStart(2, "0")}</p>
                </Space>

            ),
            dataIndex: 'name',
            key: 'name',
            width: "20%",
            sorter: true,
            sortIcon: ({ sortOrder }) => <SortIcon order={sortOrder} />,
            render: (text) => (
                <Space>
                    <ProductIcon />
                    <Link to="" className="text-[#175CD3]">{text}</Link>
                </Space>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            width: "20%",
            // sorter: true,
            // sortIcon: ({ sortOrder }) => <SortIcon order={sortOrder} />,
            render: (text) => (
                <Tooltip title={text}>
                    <p className="m-0 w-[180px] truncate">{text}</p>
                </Tooltip>
            ),
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            width: "18%",
            filters: productCategories,
            filterIcon: (filtered: boolean) => (
                <FilterIcon filtered={filtered} />
            )
        },
        {
            title: 'Sub Category',
            dataIndex: 'sub_category',
            key: 'sub_category',
            width: "18%",
            filters: subCategories,
            filterIcon: (filtered: boolean) => (
                <FilterIcon filtered={filtered} />
            )
        },
        {
            title: 'Link',
            dataIndex: 'product_links',
            key: 'product_links',
            width: "5%",
            render: (links: string[]) => (
                links?.length > 1 ? (
                    <Tooltip title={(
                        <div className="flex flex-col gap-y-[8px] p-[8px]">
                            {
                                links?.map((l: string) => (
                                    <div className="text-[12px] text-white flex items-center gap-[4px] hover:opacity-90 cursor-pointer" onClick={() => openLink(l)}>
                                        <p className="m-0 text-[12px] max-w-[200px] truncate">{l}</p>
                                        <OpenIcon color="#FFFFFF" />
                                    </div>
                                ))
                            }
                        </div>

                    )}>
                        <Space>
                            <p className="m-0">{links?.length?.toString().padStart(2, "0")}</p>
                            <p className="text-[#175CD3] text-[13px] m-0">Links</p>
                        </Space>

                    </Tooltip >
                ) : (
                    links?.length === 1 ? (
                        <div onClick={() => openLink(links[0])} className="text-[#175CD3] flex items-center gap-[4px] cursor-pointer">
                            Open
                            <OpenIcon />
                        </div>
                    ) : (
                        <></>
                    )

                )


            )
        },
        {
            title: 'Doc Requests',
            dataIndex: 'product_document_requests',
            key: 'product_document_requests',
            width: "12%",
            // sorter: true,
            // sortIcon: ({ sortOrder }) => <SortIcon order={sortOrder} />,
            render: (value: any, record: any) => (
                <Space>
                    <p className="m-0">{value?.length?.toString().padStart(2, "0")}</p>
                    {value?.length > 0 ? (
                        <div className="text-[#175CD3] cursor-pointer" onClick={() => onManageDocReq(record?.id)}>Manage</div>
                    ) : (
                        <></>
                    )}

                </Space>
            )
        },
        {
            title: 'Actions',
            dataIndex: 'actions',
            key: 'actions',
            width: "10%",
            align: "center",
            render: (_, record: any) => (
                <Dropdown menu={{ items: actionOptions(record) }}>
                    <Button icon={<ThreeDotsIcon />} type="text" onClick={(e) => e.preventDefault()} />
                </Dropdown>
            )
        },
    ]




    return (
        <div className="h-[calc(100vh-256px)] bg-white p-[20px] rounded-[8px]">
            {contextHolder}
            <div className="flex justify-between mb-[16px]">
                <Input prefix={<SearchIcon />} className="w-[250px]" placeholder="Search Products" onChange={(e) => setSearchKey(e.target.value?.trim())} />
                <Button type="primary" onClick={() => setIsAddProduct(true)}>New Product</Button>
            </div>
            <div
                ref={tableRef}
                className='h-[calc(100%-32px)] overflow-hidden w-full'
            >
                <Table
                    size="small"
                    rowKey={"id"}
                    columns={columns}
                    dataSource={data}
                    loading={loading}
                    pagination={{
                        total: totalCount,
                        pageSize: 20,
                        current: pageNumber
                    }}
                    onChange={onTableChange}
                    scroll={{ y: scrollY, x: 'max-content' }}
                />
            </div>
            <AddEditProductModal
                brand={props?.brand}
                open={isAddProduct}
                editData={isEditProduct}
                onCancel={() => onCancelAddProduct()}
                onSuccess={(data: any) => onSuccessAddProduct(data)}
            />
            <DeleteModalSimple
                title="Product"
                item={isDeleteItem}
                onCancel={() => setIsDeleteItem({})}
                onSuccess={() => onSuccessDeleteProduct()}
                api={DeleteBrandProduct}
            />
        </div>
    )
}

export default Products